/**
 * State for the checkout recovery panel — the way out we offer a buyer whose
 * payment navigation silently failed.
 *
 * Payment is initiated from a plain util rather than from React, and any of the
 * campaign flows can start one, so the panel subscribes to this module instead
 * of having the checkout URL threaded through every caller.
 */

import { getSafeRedirectUrl } from "./safeRedirect";

type Listener = (checkoutUrl: string | null) => void;

let stuckCheckoutUrl: string | null = null;
const listeners = new Set<Listener>();

const notify = () => {
    listeners.forEach((listener) => listener(stuckCheckoutUrl));
};

export const subscribeToCheckoutRecovery = (listener: Listener) => {
    listeners.add(listener);
    listener(stuckCheckoutUrl);
    return () => {
        listeners.delete(listener);
    };
};

export const showCheckoutRecovery = (checkoutUrl: string) => {
    stuckCheckoutUrl = checkoutUrl;
    notify();
};

export const dismissCheckoutRecovery = () => {
    stuckCheckoutUrl = null;
    notify();
};

export const isAndroid = (): boolean => /android/i.test(navigator.userAgent);

/**
 * Builds an intent:// URL that hands the checkout page to a real browser.
 *
 * Android WebViews dispatch intent:// to the system, so this is a working
 * escape there. No package is pinned: naming Chrome breaks devices that only
 * have Samsung Internet, and the chooser resolves a handler on its own.
 * browser_fallback_url keeps the navigation useful if nothing resolves.
 *
 * iOS has no equivalent — every browser scheme is filtered by the WebViews
 * that need escaping — so the copy-link route is all we offer there.
 */
export const buildAndroidBrowserIntent = (url: string): string | null => {
    const safeUrl = getSafeRedirectUrl(url);
    if (safeUrl === null) {
        return null;
    }
    const withoutScheme = safeUrl.replace(/^https?:\/\//, "");
    return (
        `intent://${withoutScheme}#Intent;scheme=https;` +
        `S.browser_fallback_url=${encodeURIComponent(safeUrl)};end`
    );
};
